class ReviewsSlider extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.currentIndex = 0;
    this.cardsPerView = 1;
    this.reviewCards = [];
    this.cardWidth = 0;
    this.cardGap = 0;
    this.isMobile = false;
    this.resizeObserver = null;
    this.clonesAdded = false;
    this.isAnimating = false;
    this.totalRealCards = 0;
  }

  connectedCallback() {
    this.render();
    this.initializeSlider();
    this.setupResizeObserver();
    
    this.addEventListener('shop-now', (e) => {
      console.log('Shop Now clicked for product:', e.detail.product);
      this.dispatchEvent(new CustomEvent('product-selected', {
        detail: e.detail,
        bubbles: true
      }));
    });
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          position: relative;
          margin-bottom: 3rem;
        }
        
        .slider-container {
          position: relative;
          overflow: hidden;
        }
        
        .slides-container {
          display: flex;
          gap: 1.25rem;
          align-items: stretch;
          transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: transform;
          cursor: grab;
          width: calc(100% + 20px) !important;
        }
        
        ::slotted(review-card) {
          flex-shrink: 0;
          height: 100%;
          display: flex;
          align-self: stretch;
           width: 100% !important;
           margin: 0 auto;
        }
        
        .slides-container.grabbing {
          cursor: grabbing;
          transition: none;
        }
      </style>
      
      <div class="slider-container">
        <div class="slides-container">
          <slot name="review-card"></slot>
        </div>
      </div>
    `;
  }

  initializeSlider() {
    setTimeout(() => {
      const slot = this.shadowRoot.querySelector('slot[name="review-card"]');
      this.reviewCards = slot.assignedElements();
      
      if (this.reviewCards.length === 0) return;
      
      this.totalRealCards = this.reviewCards.length;
      this.isMobile = window.innerWidth <= 767;
      this.updateCardsPerView();
      this.addClonesForInfiniteLoop();
      this.updateSlider();
      this.connectNavigation();
      
      // Listen for new cards
      if (slot) {
        slot.addEventListener('slotchange', () => {
          this.reviewCards = slot.assignedElements();
          this.totalRealCards = this.reviewCards.filter(card => !card.hasAttribute('data-clone')).length;
          this.updateCardsPerView();
          this.updateSlider();
          this.equalizeCardHeights();
        });
      }
      
      this.setupKeyboardNavigation();
      this.setupTouchNavigation();
      this.equalizeCardHeights();
    }, 100);
  }

  updateCardsPerView() {
    if (this.isMobile) {
      this.cardsPerView = 1;
    } else if (window.innerWidth >= 750) {
      const columnsAttr = this.getAttribute('data-columns');
      const columns = parseInt(columnsAttr || '3', 10);
      this.cardsPerView = Number.isFinite(columns) && columns > 0 ? columns : 3;
    } else {
      this.cardsPerView = 2;
    }
    
    // Update card width calculation
    if (this.reviewCards.length > 1) {
      const a = this.reviewCards[0].getBoundingClientRect();
      const b = this.reviewCards[1].getBoundingClientRect();
      const delta = Math.round(b.left - a.left);
      if (delta > 0) {
        this.cardWidth = delta;
        return;
      }
    }

    if (this.reviewCards.length > 0) {
      const card = this.reviewCards[0];
      const slidesContainer = this.shadowRoot.querySelector('.slides-container');
      const slidesStyle = slidesContainer ? window.getComputedStyle(slidesContainer) : null;
      const gap = slidesStyle ? (parseFloat(slidesStyle.columnGap || slidesStyle.gap) || 0) : 0;
      this.cardGap = gap;
      this.cardWidth = card.offsetWidth + this.cardGap;
    }
  }

  addClonesForInfiniteLoop() {
    if (this.clonesAdded || this.totalRealCards < this.cardsPerView) return;
    
    const slot = this.shadowRoot.querySelector('slot[name="review-card"]');
    
    // Clone first set of cards to the end
    for (let i = 0; i < this.cardsPerView; i++) {
      const clone = this.reviewCards[i].cloneNode(true);
      clone.setAttribute('data-clone', 'true');
      this.appendChild(clone);
    }
    
    // Clone last set of cards to the beginning
    for (let i = this.totalRealCards - this.cardsPerView; i < this.totalRealCards; i++) {
      const clone = this.reviewCards[i].cloneNode(true);
      clone.setAttribute('data-clone', 'true');
      this.insertBefore(clone, this.firstChild);
    }
    
    this.clonesAdded = true;
    this.reviewCards = slot.assignedElements();
    
    // Start at the first real card position
    this.currentIndex = this.cardsPerView;
    
    // Jump to starting position without animation
    const slidesContainer = this.shadowRoot.querySelector('.slides-container');
    slidesContainer.style.transition = 'none';
    this.updateSliderPosition();
    
    // Re-enable transitions
    setTimeout(() => {
      slidesContainer.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
    }, 50);
  }

  updateSliderPosition() {
    const slidesContainer = this.shadowRoot.querySelector('.slides-container');
    if (!slidesContainer) return;

    const offset = -this.currentIndex * this.cardWidth;
    slidesContainer.style.transform = `translateX(${offset}px)`;
    this.updateButtonStates();
  }

  handleInfiniteLoop() {
    const totalCards = this.reviewCards.length;
    const realCardsStart = this.cardsPerView;
    const realCardsEnd = this.cardsPerView + this.totalRealCards;
    
    // If we've scrolled past the end clones
    if (this.currentIndex >= realCardsEnd) {
      const slidesContainer = this.shadowRoot.querySelector('.slides-container');
      slidesContainer.style.transition = 'none';
      
      // Jump back to the beginning of real cards
      this.currentIndex = realCardsStart;
      this.updateSliderPosition();
      
      // Re-enable transition
      setTimeout(() => {
        slidesContainer.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
      }, 50);
    }
    
    // If we've scrolled past the beginning clones
    if (this.currentIndex < realCardsStart) {
      const slidesContainer = this.shadowRoot.querySelector('.slides-container');
      slidesContainer.style.transition = 'none';
      
      // Jump forward to the end of real cards
      this.currentIndex = realCardsEnd - this.cardsPerView;
      this.updateSliderPosition();
      
      // Re-enable transition
      setTimeout(() => {
        slidesContainer.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)';
      }, 50);
    }
  }

  updateSlider() {
    if (this.reviewCards.length === 0 || this.isAnimating) return;
    this.isAnimating = true;
    
    this.updateSliderPosition();
    
    // Handle infinite loop after animation
    setTimeout(() => {
      this.handleInfiniteLoop();
      this.isAnimating = false;
      this.equalizeCardHeights();
    }, 500);
  }

  nextSlide() {
    if (this.isAnimating) return;
    
    // Move forward by one page (cardsPerView)
    const pageStep = this.cardsPerView;
    this.currentIndex += pageStep;
    this.updateSlider();
  }

  prevSlide() {
    if (this.isAnimating) return;
    
    // Move backward by one page (cardsPerView)
    const pageStep = this.cardsPerView;
    this.currentIndex -= pageStep;
    this.updateSlider();
  }

  connectNavigation() {
    const prevBtn = document.getElementById('prev-btn');
    const nextBtn = document.getElementById('next-btn');
    
    if (prevBtn && nextBtn) {
      prevBtn.addEventListener('click', () => this.prevSlide());
      nextBtn.addEventListener('click', () => this.nextSlide());
      
      this.updateButtonStates();
    }
  }

  updateButtonStates() {
    const prevBtn = document.getElementById('prev-btn');
    const nextBtn = document.getElementById('next-btn');
    
    // Always enabled for infinite loop
    if (prevBtn) {
      prevBtn.disabled = false;
      prevBtn.setAttribute('aria-disabled', 'false');
    }
    
    if (nextBtn) {
      nextBtn.disabled = false;
      nextBtn.setAttribute('aria-disabled', 'false');
    }
  }

  setupKeyboardNavigation() {
    document.addEventListener('keydown', (e) => {
      if (e.target.closest('review-card') || e.target.closest('reviews-slider')) {
        if (e.key === 'ArrowLeft') this.prevSlide();
        if (e.key === 'ArrowRight') this.nextSlide();
      }
    });
  }

  setupTouchNavigation() {
    const slidesContainer = this.shadowRoot.querySelector('.slides-container');
    if (!slidesContainer) return;
    
    let startX = 0;
    let isDragging = false;

    const handleStart = (clientX) => {
      startX = clientX;
      isDragging = true;
      slidesContainer.classList.add('grabbing');
    };

    const handleMove = (clientX) => {
      if (!isDragging) return;
      const diff = startX - clientX;
      
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.nextSlide();
        } else {
          this.prevSlide();
        }
        isDragging = false;
        slidesContainer.classList.remove('grabbing');
      }
    };

    const handleEnd = () => {
      isDragging = false;
      slidesContainer.classList.remove('grabbing');
    };

    // Mouse events
    slidesContainer.addEventListener('mousedown', (e) => {
      e.preventDefault();
      handleStart(e.clientX);
    });

    slidesContainer.addEventListener('mousemove', (e) => {
      handleMove(e.clientX);
    });

    slidesContainer.addEventListener('mouseup', handleEnd);
    slidesContainer.addEventListener('mouseleave', handleEnd);

    // Touch events
    slidesContainer.addEventListener('touchstart', (e) => {
      handleStart(e.touches[0].clientX);
    }, { passive: true });

    slidesContainer.addEventListener('touchmove', (e) => {
      if (isDragging) e.preventDefault();
      handleMove(e.touches[0].clientX);
    }, { passive: false });

    slidesContainer.addEventListener('touchend', handleEnd);
  }

  equalizeCardHeights() {
    if (this.reviewCards.length === 0) return;
    
    // Reset heights
    this.reviewCards.forEach(card => {
      card.style.height = 'auto';
    });
    
    // Calculate max height
    let maxHeight = 0;
    this.reviewCards.forEach(card => {
      const height = card.getBoundingClientRect().height;
      if (height > maxHeight) maxHeight = height;
    });
    
    // Apply max height to all cards
    if (maxHeight > 0) {
      this.reviewCards.forEach(card => {
        card.style.height = `${maxHeight}px`;
      });
    }
  }

  setupResizeObserver() {
    this.resizeObserver = new ResizeObserver(this.debounce(() => {
      this.isMobile = window.innerWidth <= 767;
      this.updateCardsPerView();
      this.updateSlider();
      this.equalizeCardHeights();
    }, 250));
    
    this.resizeObserver.observe(this);
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}

// Register the component
if (!customElements.get('reviews-slider')) {
  customElements.define('reviews-slider', ReviewsSlider);
}